import * as Sentry from '@sentry/react';
import { Store } from 'redux';

import { _ } from '../../libs';
import { BaseStore } from '../../types';

const RETRY_TIMEOUT_LIMIT = 1;
const RETRY_500_ERRORS = 3;
const TIMEOUT_STATUS = 504;
const UNAUTHORIZED_STATUS = 401;

const clientOverrideFunction = (store?: Store, actions?: any) => (func: any) => {
  const sleep = (s: number) => new Promise((r) => setTimeout(r, s * 1000));
  if (_.isEmpty(actions)) return func;

  return async (...args: any) => {
    const authenticated = (store?.getState?.() as BaseStore)?.auth?.authenticated ?? false;

    const makeCall = async (tries = 1): Promise<any> => {
      // Incremental retry
      if (tries > 1) {
        await sleep(Math.pow(2, tries - 1));
      }

      try {
        return await func(...args);
      } catch (e: any) {
        const status = e?.status;

        // if we had a connection error
        if (!status) {
          console.error('Unable to connect to Finsera. Please check your connection.', e);
          return await makeCall(tries + 1);
        }

        // If timeout and we have tries then call again
        if (status == TIMEOUT_STATUS && tries <= RETRY_TIMEOUT_LIMIT) {
          return await makeCall(tries + 1);
        }

        // If 500 error but TimeOut and we have tries then call again
        if (status !== TIMEOUT_STATUS && status >= 500 && tries <= RETRY_500_ERRORS) {
          return await makeCall(tries + 1);
        }

        // if unauthorized we will send a top message and logout the user,
        // only if the user is authenticated and has a current user
        if (status == UNAUTHORIZED_STATUS && authenticated && store?.getState()?.auth?.currentUser) {
          store?.dispatch(actions.auth.storeLogout());
          store?.dispatch(
            actions.apiErrors.addError(undefined, undefined, 'Your session has expired. Please login again.', undefined)
          );
        }

        // Dispatch 500 errors on top of page
        if (status >= 500) {
          Sentry.withScope((scope) => {
            scope.setTag('manual', 'true');
            if (e instanceof Error) Sentry.captureException(e);
            else Sentry.captureException(new Error((e as any)?.message || JSON.stringify(e)));
          });

          store?.dispatch(actions.apiErrors.addError(e.url, status, e.statusText, e.responseJSON));
        }
        throw e;
      }
    };

    return await makeCall();
  };
};

export default clientOverrideFunction;
